import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FlexSpacer } from 'ui/flexSpacer';
import { SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { ModalContextProvider, useModalContext } from 'utils/contexts/modal';
import { Table } from 'ui/table';
import { CSVLink } from 'react-csv';
import { PaginationFooter } from 'ui/paginationFooter';
import { useClientPaginatedState } from 'ui/table/hooks/useClientPaginatedState';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { InputText } from 'ui/inputs/inputText';
import { selectFeatureFlags } from 'core/userContext';
import { HiddenCSVLink } from './AllOrganizations.styled';
import { AllOrganizationsModals } from './AllOrganizationsModals';
import { fetchAllOrganizationsRequest } from '../actions';
import { selectAllOrganizationsList, selectAllOrganizationsForCSVExportList } from '../selectors';
import { COLUMNS } from '../constants';
import { AllOrganizationsTableFilterFn } from '../utils';
import type { OrgParamProps, AllOrganizationsModalState } from '../types';

const AllOrganizations = () => {
  const organizations = useSelector(selectAllOrganizationsList);
  const csvData = useSelector(selectAllOrganizationsForCSVExportList);
  const orgsData = Object.values(organizations);
  const dispatch = useDispatch();
  const csvLink = useRef<CSVLink & { link: HTMLAnchorElement }>(null);
  const { showModal } = useModalContext<AllOrganizationsModalState>();
  const {
    currentPage,
    itemsPerPage,
    numItems,
    rows,
    setCurrentPage,
    setItemsPerPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
    filter,
    setFilter,
  } = useClientPaginatedState<OrgParamProps>({
    rows: orgsData,
    sortedBy: 'id',
    filterFn: AllOrganizationsTableFilterFn,
  });

  // sync sync sync
  const { HightouchIntegration } = useSelector(selectFeatureFlags);

  const clickExportCsvButton = () => {
    csvLink.current?.link?.click();
  };

  useEffect(() => {
    dispatch(fetchAllOrganizationsRequest());
  }, [dispatch]);

  return (
    <>
      <PageHeader
        pageActions={
          <FlexSpacer gap="actionGap">
            {HightouchIntegration && (
              <SecondaryButton
                size={BUTTON_SIZES.LARGE}
                onClick={() => showModal({ action: 'organizationSyncEditModal' })}
                data-test="manage-sync-limits-button"
              >
                Manage sync limits
              </SecondaryButton>
            )}
            <SecondaryButton
              size={BUTTON_SIZES.LARGE}
              onClick={clickExportCsvButton}
              data-test="export-to-csv-button"
            >
              Export to CSV
            </SecondaryButton>
            <HiddenCSVLink
              data={csvData}
              filename={`organizations.csv`}
              ref={csvLink}
              target="_blank"
            />
            <InputText
              data-test="filter-orgs-input"
              placeholder="Filter orgs by..."
              value={filter}
              onChange={setFilter}
            />
          </FlexSpacer>
        }
      >
        Organizations
      </PageHeader>
      <Table>
        <TableHeader
          columns={COLUMNS}
          setSortDirection={setSortDirection}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          sortedBy={sortedBy}
        />
        <TableRows rows={rows} columns={COLUMNS} />
      </Table>
      <PaginationFooter
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numItems}
        onUpdate={({ page, pageSize }) => {
          setCurrentPage(page);
          setItemsPerPage(pageSize);
        }}
      />
      <AllOrganizationsModals />
    </>
  );
};

export const AllOrganizationsPage = () => (
  <ModalContextProvider>
    <AllOrganizations />
  </ModalContextProvider>
);
